import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import useProductParams from "@/src/hooks/useProductParams";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import React, { useState } from "react";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { getCaseLink, getProductData, getProductNameBy } from "@/src/services/productService";
import FinalProcessFeedbackFlexServiceClient from "./Modules/FinalProcessFeedbackFlexServiceClient";
import Button from "@mui/material/Button";
import { Link, useNavigate } from "react-router";
import _cloneDeep from "lodash/cloneDeep";
import ApiClient from "@/src/services/apiClient";
import { STATUS_COMPLETED } from "@/src/services/litigationCase/leadStatus";
import { isFlexServiceProduct } from "@/src/services/advocardService";
import Typography from "@mui/material/Typography";

const FlexServiceFeedback = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading: isLoadingProduct } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const [isLoading] = useState(false);
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data);
    },
  });
  const navigate = useNavigate();

  if (isLoadingProduct) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  if (!!product.finalClaimStatusClient || product.leadStatus !== STATUS_COMPLETED || !isFlexServiceProduct(product)) {
    return (
      <LayoutProtectedRoutes>
        <HintBox headline={"Keine Berechtigung"} sx={{ padding: "1.5rem" }}>
          <Typography sx={{ textAlign: "left" }}>Sie haben aktuell nicht die Berechtigung diese Seite aufzurufen.</Typography>
          <Button component={Link} to={getCaseLink(product)} variant={"outlined"} color={"primary"} sx={{ marginTop: "1rem" }}>
            Zurück zum Fall
          </Button>
        </HintBox>
      </LayoutProtectedRoutes>
    );
  }

  const initialValues = {
    clientReaction: null,
    finalProcessFeedbackClient: null,
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    const updateData = _cloneDeep(values);
    if (updateData.clientReaction === "closeCase") {
      updateData.finalClaimStatusClient = "fullyAgreed";
      await updateMutation.mutateAsync(
        {
          uri: getProductData(product.name, "apiUri"),
          id: product.id,
          data: updateData,
        },
        {
          // This marks the inquiry as done in BE subscriber, so collections should refetch
          onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") }),
        },
      );
    } else if (updateData.clientReaction === "continueNewCase") {
      await ApiClient.post(`/advocard/flex/reset_case/${product.productCategory}/${product.id}`);
    }

    navigate(getCaseLink(product) + "/flex/rueckmeldung/bestaetigung", { state: "sendForm" });
  };

  return (
    <LayoutProtectedRoutes>
      <HintBox headline={"Weiteres Vorgehen notwendig"}>
        <Typography>
          <strong>Wenn Sie der Meinung sind, dass Sie weiteren rechtlichen Beistand benötigen, dann können Sie uns das hier mitteilen.</strong>
          <br />
          Bitte geben Sie uns hier eine Rückmeldung darüber, wie Sie weiter vorgehen möchten.
        </Typography>
      </HintBox>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <FinalProcessFeedbackFlexServiceClient isLoading={isLoading} />
      </FormProvider>
    </LayoutProtectedRoutes>
  );
};

export default FlexServiceFeedback;
